import { useEffect } from 'react'
import { WidgetInput, Address, Country } from 'react-addressfinder'

export interface AddressMeta {
  id: string
  address_line_1: string
  address_line_2: string
  locality_name: string
  state_territory: string
  postcode: string
  full_address: string
  canonical_address: string // official address recorded by aus post
  suburb: string
  city: string
}

interface AddressFinderInputProps {
  label: string
  inputId: string
  error?: {
    message?: string // Allow message to be undefined
  },
  onChange?: (value: string, address: Address) => void
  defaultValue?: string
  required?: boolean
  placeholder?: string
}

const ADDRESS_FINDER_KEY = 'Y3Q8CJXBK96M7LAWPUVD'

const AddressFinderInput: React.FC<AddressFinderInputProps> = ({ label, inputId, error, defaultValue, onChange, placeholder, required = false }) => {
  const inputClasses = `
    transition duration-500 appearance-none font-[var(--font-weight-normal)]
    col-start-1 row-start-1 block w-full outline outline-1 outline-[var(--input-border)] rounded-md bg-white py-[11px] pl-3 pr-10 text-base text-[var(--theme-text)]
    ${error ? 'outline-[var(--theme-danger)] focus:outline-[var(--theme-danger)]' : 'focus:outline-[var(--theme-text)]'}
    placeholder:text-[var(--input-placeholder)] focus:outline-1  sm:pr-9 sm:text-lg
  `

  // The Widget doesn't have a placeholder prop, so we need to set it manually
  useEffect(() => {
    const inputElement = document.getElementById(inputId) as HTMLInputElement
    if (inputElement && placeholder) {
      inputElement.placeholder = placeholder
    }
    if (defaultValue) {
      inputElement.value = defaultValue
    }
  }, [inputId, placeholder, defaultValue])

  return (
    <div>
      <label
        htmlFor={inputId}
        className={`
          ${error ? 'text-[var(--theme-danger)]' : 'text-[var(--theme-text)]'} block pl-2.5 text-sm/6 font-[var(--font-weight-bold)] h-6 leading-4 text-pretty ${required ? 'after:ml-1.5 after:content-["*"]' : ''}
        `}
      >
        {label}
      </label>
      <div className='mt-2.5 grid grid-cols-1'>
        <WidgetInput
          addressFinderKey={ADDRESS_FINDER_KEY}
          inputClassName={inputClasses}
          country={Country.NZ}
          onSelected={(fullAddress: string, address: Address | AddressMeta) => {
            if (onChange && 'line1' in address) {
              onChange(fullAddress, address)
            }
          }}
          id={inputId}
        />
      </div>
      {error && error.message && <p className='p-0.5 text-md font-[var(--font-weight-normal)] text-[var(--theme-danger)]'>{error.message}</p>}
    </div>
  )
}

export default AddressFinderInput
